import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";
import { somethingWentWrong } from "@/helpers/utilities";
import { setToken, getToken } from "@/helpers/localStorageHelper";
import {
  getAxiosConfig,
  getAxiosConfigFormData,
} from "@/helpers/axiosConfigHelpers";
import tcontractorJSONfile from "./tcontractor.json";

export const useAuthStore = defineStore(
  "authStore",
  () => {
    // -------------------
    // State
    // -------------------
    const screenWidth = ref(window.innerWidth);
    const badWords = ref(null);
    const userVersion = ref(0);
    const translations = ref({});
    const dynamicTranslations = ref({});
    const globalJsonKeys = ref(new Set()); // Track which keys came from global.json
    const accessedTranslationKeys = ref(new Set()); // Track which translation keys are actually accessed
    const loadedComponentPaths = ref(new Set()); // Track which component paths have been loaded
    const loadedKeysByComponent = ref(new Map()); // Track which keys are loaded for each component path
    const currentPagePath = ref(null); // Track current page path for language change
    const userDetails = ref({});
    const goldOrPlatinum = ref(false);
    const isUpgradeToGoldPlatinumDialogOpen = ref(false);
    const goToComments = ref(false);
    const language = ref("english");

    // -------------------
    // Getters
    // -------------------
    const screenWidthGetter = computed(() => screenWidth.value);
    const goToCommentsGetter = computed(() => goToComments.value);
    const userDetailsGetter = computed(() => userDetails.value);
    const goldOrPlatinumGetter = computed(() => goldOrPlatinum.value);
    const isUpgradeToGoldPlatinumDialogOpenGetter = computed(
      () => isUpgradeToGoldPlatinumDialogOpen.value
    );
    const userVersionGetter = computed(() => userVersion.value);
    
    // Create a Proxy to track accessed translation keys
    // This ensures only actually used keys are visible in Vue DevTools
    // Excludes keys that exist in dynamicTranslations to avoid duplicates
    const translationsGetter = computed(() => {
      const source = translations.value;
      const accessed = accessedTranslationKeys.value;
      
      return new Proxy(source, {
        get(target, prop) {
          // First check if key exists in dynamicTranslations (takes precedence)
          if (typeof prop === 'string' && prop in dynamicTranslations.value) {
            // Don't track access for dynamic translation keys (they show in dynamicTranslations, not translations)
            return dynamicTranslations.value[prop];
          }
          // Then check if key exists in target (static translations)
          if (typeof prop === 'string') {
            // Track that this key was accessed (even if it doesn't exist yet)
            // This ensures Vue tracks the dependency and re-renders when the key is added
            accessed.add(prop);
            if (prop in target) {
              return target[prop];
            }
            // Return undefined if key doesn't exist yet, but still track it
            return undefined;
          }
          return target[prop];
        },
        ownKeys(target) {
          // Return all keys in target that are not in dynamicTranslations
          // This ensures Vue can iterate over all available keys
          return Object.keys(target).filter(key => 
            !(key in dynamicTranslations.value)
          );
        },
        has(target, prop) {
          // Check if key exists in target or has been accessed (for reactivity)
          return (prop in target || accessed.has(prop)) && !(prop in dynamicTranslations.value);
        },
        getOwnPropertyDescriptor(target, prop) {
          if (prop in target && !(prop in dynamicTranslations.value)) {
            return {
              enumerable: true,
              configurable: true,
              value: target[prop]
            };
          }
          // Return descriptor for accessed keys even if they don't exist yet (for reactivity)
          if (accessed.has(prop) && !(prop in dynamicTranslations.value)) {
            return {
              enumerable: true,
              configurable: true,
              value: undefined
            };
          }
          return undefined;
        }
      });
    });

    // -------------------
    // Mutations / Setters
    // -------------------
    function setGoToComments(newValue) {
      goToComments.value = newValue;
    }

    function setScreenWidth(width) {
      screenWidth.value = width;
    }

    function setGoldOrPlatinum(newValue) {
      goldOrPlatinum.value = newValue;
    }

    function setBadWords(words) {
      badWords.value = words;
    }

    function setUserVersion(val) {
      userVersion.value = val;
    }

    function setUserDetails(val) {
      userDetails.value = val;
    }

    function setIsUpgradeToGoldPlatinumDialogOpen(val) {
      isUpgradeToGoldPlatinumDialogOpen.value = val;
    }

    function SET_DYNAMIC_TRANSLATIONS(val) {
      dynamicTranslations.value = val;
      // Remove duplicate keys from translations (don't merge - Proxy handles access)
      // This ensures dynamicTranslations is the single source of truth and no duplicates show in Vue DevTools
      removeDuplicateKeysFromTranslations();
    }

    // -------------------
    // Actions
    // -------------------
    function updateScreenWidth() {
      screenWidth.value = window.innerWidth;
    }

    // -------------------
    // Translation file loader (lazy per-module)
    // -------------------
    // Lazy glob: returns dynamic import functions we can call selectively
    // Use a relative base so keys are predictable from this file
    const TRANSLATIONS_BASE = "../../translations";
    // Vite requires a static string literal here (no variables / template literals)
    const translationsFiles = import.meta.glob("../../translations/**/*.json");

    // Helper function to filter out keys that exist in dynamicTranslations
    // This ensures dynamicTranslations always takes precedence over static translations
    function filterOutDynamicKeys(staticTranslations) {
      const filtered = {};
      Object.keys(staticTranslations).forEach(key => {
        // Only include keys that don't exist in dynamicTranslations
        if (!(key in dynamicTranslations.value)) {
          filtered[key] = staticTranslations[key];
        }
      });
      return filtered;
    }

    // Helper function to remove duplicate keys from translations that exist in dynamicTranslations
    // This ensures dynamicTranslations is the single source of truth for those keys
    // Keys that exist in dynamicTranslations are removed from translations to avoid duplicates
    function removeDuplicateKeysFromTranslations() {
      const cleaned = {};
      Object.keys(translations.value).forEach(key => {
        // Only keep keys that are NOT in dynamicTranslations
        // Keys in dynamicTranslations should only exist there, not in translations
        if (!(key in dynamicTranslations.value)) {
          cleaned[key] = translations.value[key];
        }
      });
      translations.value = cleaned;
      
      // Also clean up accessed keys tracking for removed keys
      Object.keys(dynamicTranslations.value).forEach(key => {
        accessedTranslationKeys.value.delete(key);
      });
    }

    // Simple in-memory cache to avoid re-importing same file
    const translationCache = new Map(); // key => `${lang}:${name}`
    const missingLogged = new Set(); // avoid spamming not-found logs

    function buildCandidateKeys(lang, module) {
      // Try multiple conventions to be robust:
      // 1) mirrored path: translations/<lang>/<module>.json
      // 2) underscored filename: translations/<lang>/<module_with_underscores>.json
      // 3) basename only: translations/<lang>/<basename>.json
      // 4) case variants for each segment: as-is, camelCase, PascalCase
      const candidates = [];
      if (!module) {
        candidates.push(`${TRANSLATIONS_BASE}/${lang}/global.json`);
        return candidates;
      }

      // Normalize module from underscore to path if needed
      const moduleAsPath = module.includes("/") ? module : module.replace(/_/g, "/");

      const toCamel = (s) => s.charAt(0).toLowerCase() + s.slice(1);
      const toPascal = (s) => s.charAt(0).toUpperCase() + s.slice(1);

      const segments = moduleAsPath.split("/").filter(Boolean);
      const variantSegmentsList = segments.map((seg) => [seg, toCamel(seg), toPascal(seg)]);

      // Generate combined case variants
      const combineVariants = () => {
        // Start with one empty variant and expand
        let variants = [ [] ];
        for (const segVariants of variantSegmentsList) {
          const next = [];
          for (const v of variants) {
            for (const sv of segVariants) {
              next.push([ ...v, sv ]);
            }
          }
          variants = next;
        }
        return variants.map(v => v.join("/"));
      };

      const pathVariants = Array.from(new Set([moduleAsPath, ...combineVariants()]));

      for (const pv of pathVariants) {
        const underscored = pv.replace(/\//g, "_");
        const parts = pv.split("/");
        const base = parts[parts.length - 1];
        // Prefer underscore-first to match your storage convention
        candidates.push(`${TRANSLATIONS_BASE}/${lang}/${underscored}.json`);
        // Also try with trailing underscore variant your repo uses in some files
        candidates.push(`${TRANSLATIONS_BASE}/${lang}/${underscored}_.json`);
        // Fallbacks
        candidates.push(`${TRANSLATIONS_BASE}/${lang}/${pv}.json`);
        candidates.push(`${TRANSLATIONS_BASE}/${lang}/${base}.json`);
        // Basename with trailing underscore as a final attempt
        candidates.push(`${TRANSLATIONS_BASE}/${lang}/${base}_.json`);
      }
      return Array.from(new Set(candidates));
    }

    // Load used keys directly from tcontractor.json based on component path
    // This loads translations dynamically from the JSON file without requiring page reload
    function loadUsedKeysFromJSON(componentPath) {
      if (!componentPath || !Array.isArray(tcontractorJSONfile)) {
        return {};
      }

      const lang = language.value || localStorage.getItem("lang") || "english";
      const langKey = lang === 'spanish' ? 'mexican_spanish' : 'english';
      const usedKeys = {};

      // Normalize component path for matching - remove .vue extension and normalize slashes
      const normalizedPath = componentPath.replace(/\\/g, "/").replace(/\.vue$/i, "");
      const pathWithVue = `${normalizedPath}.vue`;
      
      // Create all possible path variants for matching
      const pathVariants = [
        normalizedPath,           // Components/tCon/Menu_HamburgerMenu
        pathWithVue,              // Components/tCon/Menu_HamburgerMenu.vue
        componentPath.replace(/\\/g, "/"), // Original path as-is
      ];

      tcontractorJSONfile.forEach(item => {
        if (!item.key || !item.used_in || !Array.isArray(item.used_in)) return;
        
        const isUsedInComponent = item.used_in.some(usedInPath => {
          if (!usedInPath) return false;
          
          // Normalize the used_in path - handle both forward and backslashes, trim whitespace
          const normalizedUsedIn = String(usedInPath).replace(/\\/g, "/").trim();
          const normalizedUsedInNoExt = normalizedUsedIn.replace(/\.vue$/i, "").trim();
          
          // Check all variants for matches
          for (const variant of pathVariants) {
            const variantNormalized = String(variant).trim();
            const variantNoExt = variantNormalized.replace(/\.vue$/i, "").trim();
            
            // Exact match (with or without .vue) - case sensitive first for performance
            if (normalizedUsedIn === variantNormalized || normalizedUsedInNoExt === variantNoExt) {
              return true;
            }
            
            // Match if paths are the same when normalized (case-insensitive)
            if (variantNoExt && normalizedUsedInNoExt && 
                variantNoExt.toLowerCase() === normalizedUsedInNoExt.toLowerCase()) {
              return true;
            }
            
            // Match if one ends with the other (for partial matches)
            // This handles cases where paths might have slight variations
            if (variantNoExt && normalizedUsedInNoExt) {
              if (variantNoExt.endsWith(normalizedUsedInNoExt) || 
                  normalizedUsedInNoExt.endsWith(variantNoExt)) {
                return true;
              }
            }
          }
          
          return false;
        });
        
        if (isUsedInComponent && item[langKey]) {
          usedKeys[item.key] = item[langKey];
        }
      });

      return usedKeys;
    }

    // Filter static JSON file keys (global.json only) based on component path using tcontractor.json
    // NOTE: This filtering ONLY applies to static JSON files, NOT dynamic database translations
    function filterGlobalKeysByComponent(allGlobalKeys, componentPath) {
      if (!componentPath || !Array.isArray(tcontractorJSONfile)) {
        return allGlobalKeys;
      }

      // Find keys in tcontractor.json that are used in this component
      const usedKeys = new Set();
      tcontractorJSONfile.forEach(item => {
        if (!item.key || !item.used_in || !Array.isArray(item.used_in)) return;
        
        const isUsedInComponent = item.used_in.some(path => {
          // Exact match
          if (path === componentPath) return true;
          // Match with .vue extension
          if (path === `${componentPath}.vue`) return true;
          // Match without extension
          if (path.replace(/\.vue$/, '') === componentPath) return true;
          // Match if component path ends with the used_in path
          if (componentPath.endsWith(path) || componentPath.endsWith(path.replace(/\.vue$/, ''))) return true;
          return false;
        });
        
        if (isUsedInComponent && allGlobalKeys[item.key] !== undefined) {
          usedKeys.add(item.key);
        }
      });

      // Filter static global.json to only include keys used in this component
      const filtered = {};
      usedKeys.forEach(key => {
        if (allGlobalKeys[key] !== undefined) {
          filtered[key] = allGlobalKeys[key];
        }
      });

      return filtered;
    }

    // Load static JSON translation files (NOT dynamic database translations)
    // Only filters static global.json when componentPath is provided
    async function loadTranslationFile(lang, module = null, componentPath = null) {
      const cacheKey = `${lang}:${module || "global"}:${componentPath || "all"}`;
      if (translationCache.has(cacheKey)) return translationCache.get(cacheKey);

      const candidates = buildCandidateKeys(lang, module);
      const allKeys = Object.keys(translationsFiles);
      for (const candidate of candidates) {
        const suffix = candidate.startsWith(TRANSLATIONS_BASE)
          ? candidate.slice(TRANSLATIONS_BASE.length)
          : candidate;
        const matchedKey = allKeys.find((k) => k.endsWith(suffix));
        if (matchedKey) {
          try {
            const mod = await translationsFiles[matchedKey]();
            const data = (mod && (mod.default || mod)) || {};
            
            // ONLY filter static global.json when component path is provided
            // Module-specific JSON files and dynamic translations are NOT filtered
            if (!module && componentPath) {
              const filtered = filterGlobalKeysByComponent(data, componentPath);
              translationCache.set(cacheKey, filtered);
              return filtered;
            }
            
            translationCache.set(cacheKey, data);
            return data;
          } catch (e) {
            console.warn(`Failed to import translation: ${matchedKey}`, e);
          }
        }
      }
      // Prefer showing the underscore-style expected path in the log
      const preferred = candidates.find(c => /_[^/]*\.json$/.test(c)) || candidates[0];
      const display = preferred?.replace(`${TRANSLATIONS_BASE}/`, "");
      const missKey = `${lang}:${module || "global"}`;
      if (!missingLogged.has(missKey)) {
        // console.info(`Translation file not found: ${display || (module || "global")}`);
        missingLogged.add(missKey);
      }
      translationCache.set(cacheKey, {});
      return {};
    }

    // -------------------
    // Set translations dynamically (static JSON files only, NOT dynamic database translations)
    // -------------------
    async function setTranslations(lang, modules = [], options = {}) {
      const { componentPath = null, loadGlobal = true } = options;
      
      const nextLang = lang;
      const isLanguageChange = language.value !== nextLang;
      localStorage.setItem("lang", nextLang);

      // Per-page loading: When language changes, only update existing keys, don't load new ones
      if (isLanguageChange) {
        // Get all currently loaded keys (from all pages)
        const allLoadedKeys = new Set();
        loadedKeysByComponent.value.forEach((keys) => {
          keys.forEach(key => allLoadedKeys.add(key));
        });
        
        // Also include keys from globalJsonKeys (they were loaded from global.json)
        globalJsonKeys.value.forEach(key => allLoadedKeys.add(key));
        
        // Also include keys currently in translations (for safety - includes keys from module files)
        Object.keys(translations.value).forEach(key => allLoadedKeys.add(key));
        
        // console.log(`[setTranslations] Language change detected. Updating ${allLoadedKeys.size} existing keys only.`);
        
        // Build next translations buffer - only reload existing keys in new language
        const next = {};
        const langKey = nextLang === 'spanish' ? 'mexican_spanish' : 'english';
        
        // Reload all existing keys from tcontractor.json in new language
        for (const key of allLoadedKeys) {
          // Try to reload this key from tcontractor.json in new language
          const keyData = tcontractorJSONfile.find(item => item.key === key);
          
          if (keyData && keyData[langKey]) {
            // Key exists in tcontractor.json, use new language value
            next[key] = keyData[langKey];
          } else {
            // Key doesn't exist in tcontractor.json, might be from global.json or module files
            // For now, keep old value - we'll reload it from global.json/module files below
            // But only if it was in translations before
            if (translations.value[key]) {
              next[key] = translations.value[key]; // Keep old value temporarily
            }
          }
        }
        
        // Now reload keys from global.json and module files for keys that weren't in tcontractor.json
        // But only for keys that are already in next (already loaded)
        const keysToReloadFromFiles = Object.keys(next).filter(key => {
          // Check if this key was from global.json or module files (not in tcontractor.json)
          const keyData = tcontractorJSONfile.find(item => item.key === key);
          return !keyData; // If not in tcontractor.json, reload from files
        });
        
        if (keysToReloadFromFiles.length > 0 && loadGlobal) {
          // Reload global.json and filter to only include keys we need
          const globalTrans = await loadTranslationFile(nextLang, null, componentPath);
          const filteredGlobalTrans = filterOutDynamicKeys(globalTrans);
          
          // Only update keys that are already loaded
          keysToReloadFromFiles.forEach(key => {
            if (filteredGlobalTrans[key]) {
              next[key] = filteredGlobalTrans[key];
            }
          });
        }
        
        // Clear global.json keys tracking and accessed keys when language changes
        // But preserve the tracking of which keys were loaded for which component
        globalJsonKeys.value.clear();
        accessedTranslationKeys.value.clear();
        
        // Atomically switch language and translations
        language.value = nextLang;
        translations.value = next;
        
        // console.log(`[setTranslations] Updated ${Object.keys(next).length} keys for language change (no new keys added)`);
        return;
      }
      
      // Not a language change - normal loading behavior
      // Build next translations buffer starting from current to avoid blanks
      const next = { ...translations.value };

      // Load static global.json - filter by component path if provided (only if loadGlobal is true)
      // NOTE: This only loads static JSON files, NOT dynamic database translations
      if (loadGlobal) {
        const globalTrans = await loadTranslationFile(nextLang, null, componentPath);
        const filteredGlobalTrans = filterOutDynamicKeys(globalTrans);
        Object.assign(next, filteredGlobalTrans);
      }

      // Load static module/component-specific JSON translation files (NOT filtered)
      const uniqueModules = Array.from(new Set(modules.filter(Boolean)));
      for (const module of uniqueModules) {
        const moduleTrans = await loadTranslationFile(nextLang, module);
        const filteredModuleTrans = filterOutDynamicKeys(moduleTrans);
        Object.assign(next, filteredModuleTrans);
      }

      // Atomically switch language and translations (preserves old keys until overwritten)
      language.value = nextLang;
      translations.value = next;
    }

    // -------------------
    // Fetch translations
    // -------------------
    async function fetchTranslations(modules = []) {
      const lang = localStorage.getItem("lang") || "english";
      localStorage.setItem("lang", lang);
      // Per-page loading: Don't load global.json initially
      // Only load translations when components mount (via mixin)
      // This ensures only page-specific keys are loaded
      await setTranslations(lang, modules, { loadGlobal: false, componentPath: null });
    }

    // Backward-compatibility: some callers use a misspelled API name
    async function setTranlations(lang, modules = []) {
      await setTranslations(lang, modules);
    }

    // Append additional module translations without resetting current ones
    async function appendTranslations(modules = []) {
      const lang = language.value || localStorage.getItem("lang") || "english";
      const uniqueModules = Array.from(new Set(modules.filter(Boolean)));
      for (const module of uniqueModules) {
        const moduleTrans = await loadTranslationFile(lang, module);
        const filteredModuleTrans = filterOutDynamicKeys(moduleTrans);
        translations.value = { ...translations.value, ...filteredModuleTrans };
      }
    }

    // -------------------
    // Component-aware loading (per-component only by default)
    // -------------------
    function inferModuleFromComponentInstance(componentInstance) {
      // Works for Vue SFCs; __file contains absolute path to .vue during dev and in sourcemaps
      // Handle both Options API (this) and Composition API instances
      let file = null;
      if (componentInstance) {
        // Try Composition API style first
        file = componentInstance?.type?.__file || componentInstance?.__file;
        // Try Options API style (this.$ or this)
        if (!file && componentInstance.$) {
          file = componentInstance.$.type?.__file || componentInstance.$.__file;
        }
        // Try direct access for Options API
        if (!file && componentInstance.__file) {
          file = componentInstance.__file;
        }
        // Try accessing via $options for Options API components
        if (!file && componentInstance.$options && componentInstance.$options.__file) {
          file = componentInstance.$options.__file;
        }
      }
      if (!file) return null;
      // Expect something like: /.../resources/js/Pages/Profile/Partials/BillingSettingsTab.vue
      const marker = "/resources/js/";
      const idx = file.lastIndexOf(marker);
      if (idx === -1) return null;
      const relative = file.substring(idx + marker.length);
      const withoutExt = relative.replace(/\.(vue|js|ts|tsx)$/i, "");
      // Convert Windows backslashes just in case
      return withoutExt.replace(/\\/g, "/");
    }

    // Load static JSON translations for a specific component
    // NOTE: Only filters static global.json, NOT dynamic database translations
    // Uses lazy loading - keys are loaded on-demand when accessed
    async function loadComponentTranslations(componentInstance, options = { withGlobal: true, clear: false }) {
      const lang = language.value || localStorage.getItem("lang") || "english";
      const moduleKey = inferModuleFromComponentInstance(componentInstance);
      
      // If we can't infer the component path, try to get it from the component itself
      let componentPath = moduleKey;
      if (!componentPath && componentInstance) {
        // Try to extract path directly from component
        const file = componentInstance?.type?.__file || 
                     componentInstance?.__file || 
                     componentInstance?.$options?.__file ||
                     (componentInstance.$ && (componentInstance.$.type?.__file || componentInstance.$.__file));
        
        if (file) {
          const marker = "/resources/js/";
          const idx = file.lastIndexOf(marker);
          if (idx !== -1) {
            const relative = file.substring(idx + marker.length);
            componentPath = relative.replace(/\.(vue|js|ts|tsx)$/i, "").replace(/\\/g, "/");
          }
        }
      }
      
      // If we still can't infer the component path, don't load anything (don't load all keys)
      if (!componentPath) {
        console.warn('loadComponentTranslations: Could not infer component path');
        return;
      }
      
      if (options.clear) {
        // Only clear static translations, preserve dynamic translations from database
        const preservedDynamic = {};
        Object.keys(dynamicTranslations.value).forEach(key => {
          if (translations.value[key]) {
            preservedDynamic[key] = translations.value[key];
          }
        });
        translations.value = preservedDynamic;
        // Clear accessed keys tracking when clearing
        accessedTranslationKeys.value.clear();
      }
      
      // Set current page path if this is a page component
      if (componentPath && componentPath.startsWith('Pages/')) {
        setCurrentPagePath(componentPath);
      }
      
      // Load used keys from tcontractor.json first (without page reload)
      // Only load if this component path hasn't been loaded before
      if (!loadedComponentPaths.value.has(componentPath)) {
        const usedKeysFromJSON = loadUsedKeysFromJSON(componentPath);
        const keyCount = Object.keys(usedKeysFromJSON).length;
        if (keyCount > 0) {
          // Filter out keys that exist in dynamicTranslations before merging
          const filteredUsedKeys = filterOutDynamicKeys(usedKeysFromJSON);
          
          // Only add keys that don't already exist in translations (avoid duplicates)
          const newKeys = {};
          let addedCount = 0;
          Object.keys(filteredUsedKeys).forEach(key => {
            // Only add if key doesn't exist in translations yet
            if (!(key in translations.value)) {
              newKeys[key] = filteredUsedKeys[key];
              addedCount++;
            }
          });
          
          // Merge only new keys (dynamicTranslations already takes precedence)
          if (addedCount > 0) {
            translations.value = { ...translations.value, ...newKeys };
            // Track which keys were loaded for this component
            loadedKeysByComponent.value.set(componentPath, new Set(Object.keys(newKeys)));
            // console.log(`[loadComponentTranslations] Added ${addedCount}/${keyCount} new keys for: ${componentPath}`, {
            //   newKeys: Object.keys(newKeys),
            //   skipped: keyCount - addedCount
            // });
          } else {
            // console.log(`[loadComponentTranslations] All ${keyCount} keys already exist for: ${componentPath}`);
          }
          
          // Mark this component path as loaded
          loadedComponentPaths.value.add(componentPath);
        } else {
          // console.warn(`[loadComponentTranslations] No keys found in tcontractor.json for: ${componentPath}`);
          // Still mark as loaded to avoid repeated lookups
          loadedComponentPaths.value.add(componentPath);
        }
      } else {
        // console.log(`[loadComponentTranslations] Component path already loaded, skipping: ${componentPath}`);
      }
      
      // Per-page loading: Only load global.json keys for page components, not every component
      // Check if this is a page component (starts with "Pages/")
      const isPageComponent = componentPath && componentPath.startsWith('Pages/');
      
      if (options.withGlobal && isPageComponent) {
        // Only load global.json for page components
        // Don't remove previously loaded global.json keys - allow sharing between pages
        // Load only static global.json keys used by this page component (filtered by component path)
        const globalTrans = await loadTranslationFile(lang, null, componentPath);
        const filteredGlobalTrans = filterOutDynamicKeys(globalTrans);
        
        // Only add new global.json keys (avoid duplicates)
        const newGlobalKeys = {};
        Object.keys(filteredGlobalTrans).forEach(key => {
          // Only add if key doesn't exist in translations yet
          if (!(key in translations.value)) {
            newGlobalKeys[key] = filteredGlobalTrans[key];
            globalJsonKeys.value.add(key);
          }
        });
        
        if (Object.keys(newGlobalKeys).length > 0) {
          translations.value = { ...translations.value, ...newGlobalKeys };
          // console.log(`[loadComponentTranslations] Added ${Object.keys(newGlobalKeys).length} global.json keys for page: ${componentPath}`);
        }
      } else if (options.withGlobal && !isPageComponent) {
        // For non-page components, don't load global.json
        // They will use keys already loaded by the page component
        // console.log(`[loadComponentTranslations] Skipping global.json for non-page component: ${componentPath}`);
      }
      
      // Load static module-specific JSON file (not filtered, component-specific already)
      const moduleTrans = await loadTranslationFile(lang, componentPath);
      const filteredModuleTrans = filterOutDynamicKeys(moduleTrans);
      translations.value = { ...translations.value, ...filteredModuleTrans };
    }
    
    // Load used keys from tcontractor.json for a component path (called on route changes)
    // Only loads keys that haven't been loaded before (per-page loading)
    function loadUsedKeysForRoute(componentPath) {
      if (!componentPath) {
        // console.warn('loadUsedKeysForRoute: No component path provided');
        return;
      }
      
      // Set current page path if this is a page component
      if (componentPath && componentPath.startsWith('Pages/')) {
        setCurrentPagePath(componentPath);
      }
      
      // Check if this component path has already been loaded
      if (loadedComponentPaths.value.has(componentPath)) {
        // console.log(`[loadUsedKeysForRoute] Component path already loaded, skipping: ${componentPath}`);
        return;
      }
      
      const usedKeysFromJSON = loadUsedKeysFromJSON(componentPath);
      const keyCount = Object.keys(usedKeysFromJSON).length;
      
      if (keyCount > 0) {
        // Filter out keys that exist in dynamicTranslations before merging
        const filteredUsedKeys = filterOutDynamicKeys(usedKeysFromJSON);
        
        // Only add keys that don't already exist in translations (avoid duplicates)
        const newKeys = {};
        let addedCount = 0;
        Object.keys(filteredUsedKeys).forEach(key => {
          // Only add if key doesn't exist in translations yet
          if (!(key in translations.value)) {
            newKeys[key] = filteredUsedKeys[key];
            addedCount++;
          }
        });
        
        // Merge only new keys (dynamicTranslations already takes precedence)
        // Create a new object to ensure Vue reactivity
        if (addedCount > 0) {
          translations.value = { ...translations.value, ...newKeys };
          // Track which keys were loaded for this component
          loadedKeysByComponent.value.set(componentPath, new Set(Object.keys(newKeys)));
          // console.log(`[loadUsedKeysForRoute] Added ${addedCount}/${keyCount} new keys for component: ${componentPath}`, {
          //   newKeys: Object.keys(newKeys),
          //   skipped: keyCount - addedCount
          // });
        } else {
          // console.log(`[loadUsedKeysForRoute] All ${keyCount} keys already exist for component: ${componentPath}`);
        }
        
        // Mark this component path as loaded
        loadedComponentPaths.value.add(componentPath);
      } else {
        // console.warn(`[loadUsedKeysForRoute] No keys found in tcontractor.json for component: ${componentPath}`);
        // Still mark as loaded to avoid repeated lookups
        loadedComponentPaths.value.add(componentPath);
      }
    }
    
    // Clean up unused translation keys (only keep accessed keys)
    // NOTE: This is now disabled for per-page loading - we keep all loaded keys
    // This should be called after components have rendered and accessed their translations
    function cleanupUnusedTranslations() {
      // DISABLED: Don't clean up translations for per-page loading
      // We want to keep all loaded keys even if not currently accessed
      // This allows returning to previously visited pages without reloading
      
      // Keep all translations (both accessed and loaded)
      // Only remove keys that are truly unused across all pages
      
      const accessed = accessedTranslationKeys.value;
      const cleaned = {};
      
      // Keep all dynamic translations (never filter these)
      Object.keys(dynamicTranslations.value).forEach(key => {
        if (translations.value[key]) {
          cleaned[key] = translations.value[key];
        }
      });
      
      // Keep all loaded static translation keys (don't filter by access)
      // This ensures keys remain available when navigating between pages
      Object.keys(translations.value).forEach(key => {
        // Keep key if it's been accessed OR if it's a dynamic translation
        // OR if it's been loaded for any component (per-page loading)
        if (accessed.has(key) || dynamicTranslations.value[key]) {
          cleaned[key] = translations.value[key];
        } else {
          // Check if this key was loaded for any component
          let keyLoadedForComponent = false;
          loadedKeysByComponent.value.forEach((keys, componentPath) => {
            if (keys.has(key)) {
              keyLoadedForComponent = true;
            }
          });
          
          // Keep key if it was loaded for any component
          if (keyLoadedForComponent) {
            cleaned[key] = translations.value[key];
          }
        }
      });
      
      // Only update if we actually removed something (to avoid unnecessary reactivity)
      const currentKeys = Object.keys(translations.value);
      const cleanedKeys = Object.keys(cleaned);
      if (currentKeys.length !== cleanedKeys.length) {
        translations.value = cleaned;
        // console.log(`[cleanupUnusedTranslations] Kept ${cleanedKeys.length}/${currentKeys.length} keys`);
      }
    }

    // Fetch dynamic translations from database API
    // Per-page loading: Only loads dynamic translations for CURRENT PAGE's keys
    async function fetchDynamicTranslations(payload) {
      try {
        // Get current page keys if available (for per-page loading)
        const currentPageKeys = new Set();
        if (currentPagePath.value && loadedKeysByComponent.value.has(currentPagePath.value)) {
          loadedKeysByComponent.value.get(currentPagePath.value).forEach(key => {
            currentPageKeys.add(key);
          });
          // console.log(`[fetchDynamicTranslations] Found ${currentPageKeys.size} keys for current page: ${currentPagePath.value}`);
        } else {
          // console.warn(`[fetchDynamicTranslations] No current page keys found. currentPagePath: ${currentPagePath.value}, hasKeys: ${currentPagePath.value ? loadedKeysByComponent.value.has(currentPagePath.value) : 'N/A'}`);
        }
        
        // Build params - prioritize specific keys over translation_key for per-page loading
        let params = {
          lang: payload.lang,
        };
        
        // If we have current page keys and translation_key is provided, use keys instead
        // This ensures API only returns translations for current page, not all translations for that key
        if (currentPageKeys.size > 0 && payload.translation_key) {
          // Use specific keys instead of translation_key to avoid loading all translations
          params.keys = Array.from(currentPageKeys);
          // console.log(`[fetchDynamicTranslations] Using ${currentPageKeys.size} specific keys instead of translation_key for per-page loading`);
        } else if (payload.keys && Array.isArray(payload.keys) && payload.keys.length > 0) {
          // If keys are explicitly provided, use them
          params.keys = payload.keys;
        } else if (payload.translation_key) {
          // Only use translation_key if no current page keys are available (initial load)
          params.translation_key = payload.translation_key;
          // console.log(`[fetchDynamicTranslations] Using translation_key (initial load, no current page keys yet)`);
        } else if (currentPageKeys.size > 0) {
          // If no translation_key or keys provided, but we have current page keys, use them
          params.keys = Array.from(currentPageKeys);
          // console.log(`[fetchDynamicTranslations] Using ${currentPageKeys.size} current page keys`);
        }
        
        const response = await axios.get(`/api/translations`, {
          params,
        });
        if (response.data.translations) {
          localStorage.setItem("lang", payload.lang);
          
          // Determine which keys to update based on current page
          // IMPORTANT: Only update keys that are already loaded for the current page
          let keysToUpdate = new Set();
          
          if (currentPageKeys.size > 0) {
            // Use current page keys if available - this is the correct behavior
            currentPageKeys.forEach(key => keysToUpdate.add(key));
            // console.log(`[fetchDynamicTranslations] Using ${currentPageKeys.size} current page keys for language change`);
          } else if (params.keys && Array.isArray(params.keys) && params.keys.length > 0) {
            // Use explicitly provided keys
            params.keys.forEach(key => keysToUpdate.add(key));
            // console.log(`[fetchDynamicTranslations] Using ${params.keys.length} explicitly provided keys`);
          } else {
            // No current page keys found and no explicit keys provided
            // This should only happen on initial load when translation_key is used
            // For language changes, we should have current page keys, so skip update
            // console.warn(`[fetchDynamicTranslations] No current page keys found and no explicit keys provided. This might be initial load.`);
            // Only proceed if this is an initial load (translation_key was used)
            if (payload.translation_key) {
              // Initial load: use all keys from response
              Object.keys(response.data.translations).forEach(key => keysToUpdate.add(key));
              // console.log(`[fetchDynamicTranslations] Initial load: using all ${keysToUpdate.size} keys from translation_key`);
            } else {
              // Language change without current page keys - skip update to avoid loading all translations
              // console.warn(`[fetchDynamicTranslations] Language change detected but no current page keys. Skipping update.`);
              return response;
            }
          }
          
          // Filter translations to only include keys we want to update
          const filteredTranslations = {};
          keysToUpdate.forEach(key => {
            if (response.data.translations[key]) {
              filteredTranslations[key] = response.data.translations[key];
            }
          });
          
          // When language changes, we want to update only current page's keys
          // But preserve other pages' keys that are already loaded (don't remove them)
          // So we merge: keep existing dynamicTranslations, but update only the keys we filtered
          const updatedDynamicTranslations = { ...dynamicTranslations.value };
          Object.keys(filteredTranslations).forEach(key => {
            updatedDynamicTranslations[key] = filteredTranslations[key];
          });
          
          SET_DYNAMIC_TRANSLATIONS(updatedDynamicTranslations);
          // console.log(`[fetchDynamicTranslations] Updated ${Object.keys(filteredTranslations).length} dynamic translations for current page (total in store: ${Object.keys(updatedDynamicTranslations).length})`);
          
          return response;
        }
      } catch (err) {
        somethingWentWrong("Error Fetching translations");
        throw err;
      }
    }
    
    // Set current page path (called when page component mounts)
    function setCurrentPagePath(componentPath) {
      if (componentPath) {
        currentPagePath.value = componentPath;
        // console.log(`[setCurrentPagePath] Current page set to: ${componentPath}`);
      }
    }

    // -------------------
    // Bad words
    // -------------------
    async function fetchBadWords() {
      const localStorageKey = "badWords";
      const storedBadWords = localStorage.getItem(localStorageKey);

      if (storedBadWords) {
        setBadWords(new Set(JSON.parse(storedBadWords)));
      } else if (badWords.value === null) {
        try {
          const response = await axios.get("/api/badwords");
          const badWordsSet = new Set(
            response.data.map((word) => word.toLowerCase())
          );
          setBadWords(badWordsSet);
          localStorage.setItem(
            localStorageKey,
            JSON.stringify([...badWordsSet])
          );
        } catch (error) {
          throw error;
        }
      }
    }

    // -------------------
    // Auth / User
    // -------------------
    async function getTokenAction() {
      try {
        const response = await axios.post(`/tokens/create`);
        if (response.data) {
          setToken(response.data.token);
          await fetchUserVersion();
          await fetchUserDetails();
        }
      } catch (err) {
        somethingWentWrong("Wrong in token creation");
        throw err;
      }
    }

    async function fetchUserVersion() {
      if (!getToken()) return;
      try {
        const versionResponse = await axios.get(`/api/user-version`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        });
        if (versionResponse.data) {
          setUserVersion(versionResponse.data.logged_in_user_version);
        }
      } catch (err) {
        somethingWentWrong("Error fetching user version");
      }
    }

    async function fetchUserDetails() {
      if (!getToken()) return;
      try {
        const response = await axios.get(`/api/user/version-details`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        });
        if (response.data) {
          setUserDetails(response.data.versionDetails);
        }
      } catch (err) {
        somethingWentWrong("Error fetching user details");
      }
    }

    // -------------------
    // Other API methods
    // -------------------
    async function changeEmail(formData) {
      try {
        return await axios.post(
          "/api/settings/change-email",
          formData,
          getAxiosConfig()
        );
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function verifyEmail(formData) {
      try {
        return await axios.post(
          "/api/settings/verify-email",
          formData,
          getAxiosConfig()
        );
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function findContractors(params) {
      try {
        return await axios.get(`/api/sub-finder/find-contractors`, {
          params,
          ...getAxiosConfig(),
        });
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function saveContractorNotes(contractorId, notes) {
      try {
        return await axios.post(
          `/api/sub-finder/${contractorId}/preference-and-notes`,
          notes,
          getAxiosConfig()
        );
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function createRedFlag(newRedFlag) {
      try {
        return await axios.post(`/api/red-flags`, newRedFlag, getAxiosConfig());
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function updateRedFlag(redFlagId, updatedRedFlagData) {
      try {
        return await axios.put(
          `/api/red-flags/${redFlagId}`,
          updatedRedFlagData,
          getAxiosConfig()
        );
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function deleteRedFlag(redFlagId) {
      try {
        return await axios.delete(
          `/api/red-flags/${redFlagId}`,
          getAxiosConfig()
        );
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function fetchAllVersions() {
      try {
        return await axios.get(`api/all-versions`);
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function createToken() {
      try {
        return await axios.post(`/tokens/create`);
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function fetchContent(url) {
      try {
        return await axios.get(url);
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function getRequest(url) {
      try {
        return await axios.get(url, getAxiosConfig());
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    async function saveContent(url, content) {
      try {
        return await axios.post(url, { content });
      } catch (error) {
        somethingWentWrong(error.response.data.message);
        throw error;
      }
    }

    // -------------------
    // Return state, getters, actions
    // -------------------
    return {
      // State
      screenWidth,
      badWords,
      userVersion,
      translations,
      dynamicTranslations,
      userDetails,
      goldOrPlatinum,
      isUpgradeToGoldPlatinumDialogOpen,
      goToComments,
      language,

      // Getters
      screenWidthGetter,
      goToCommentsGetter,
      userDetailsGetter,
      goldOrPlatinumGetter,
      isUpgradeToGoldPlatinumDialogOpenGetter,
      userVersionGetter,
      translationsGetter,

      // Mutations / Actions
      setGoToComments,
      setScreenWidth,
      setGoldOrPlatinum,
      setBadWords,
      setTranslations,
      setUserVersion,
      setUserDetails,
      setIsUpgradeToGoldPlatinumDialogOpen,
      SET_DYNAMIC_TRANSLATIONS,
      // Backward-compat
      setTranlations,

      // Other Actions
      updateScreenWidth,
      fetchBadWords,
      getTokenAction,
      fetchTranslations,
      fetchUserVersion,
      fetchUserDetails,
      changeEmail,
      verifyEmail,
      findContractors,
      saveContractorNotes,
      updateRedFlag,
      deleteRedFlag,
      createRedFlag,
      fetchAllVersions,
      createToken,
      fetchContent,
      getRequest,
      saveContent,
      fetchDynamicTranslations,
      appendTranslations,
      loadComponentTranslations,
      cleanupUnusedTranslations,
      loadUsedKeysForRoute,
      loadUsedKeysFromJSON,
      setCurrentPagePath,
    };
  },
  { persist: true }
);
